import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { initialOrderCreate, sessionCall, setReferrer, redeemSiteToken, setSiteToken } from '../../store/action/OrderAction';
import ServiceabilityCheck from './ServiceabilityCheck';
import VasOnly from './VasOnly';
import { BackdropLoader } from '../common/Loader';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import MobileOnly from './MobileOnly';
import { OrderStatus } from '../../util/util';
import { useSessionData } from '../../hooks/order';
import { getConfigs } from '../../config';
import { appendInvocaTestModeParam } from '../../util/invocaTestMode';
import { isUndefinedNullOrEmpty, setSessionOrderAccessToken, parseRedemptionClaims, FF_PROMO_CODE, FF_SOURCE } from '../../common/common';
import { sessionKeys } from '../../helpers/sessionKeysEnum';
import useLeadsSDK from '../../hooks/useLeadsSDK';
import { sanitizeTrackingReferrer } from '../../helpers/trackingAttrs';


const config = getConfigs();

// The referring employee's address is deliberately NOT pushed to the dataLayer - GTM forwards
// to third-party tags. Attribution travels on the order payload only.
const pushFFEvent = (event, payload = {}) => {
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...payload });
};

// Single-use tokens must not linger in the URL, browser history, or any Referer header sent
// to a third party once they have been spent.
const stripSiteTokenFromUrl = () => {
  const { history, location: browserLocation } = window;
  if (!history?.replaceState || !browserLocation) {
    return;
  }
  const strip = (search) => {
    const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search);
    params.delete('site-token');
    params.delete('site_token');
    const next = params.toString();
    return next ? `?${next}` : '';
  };
  const nextSearch = strip(browserLocation.search || '');
  let nextHash = browserLocation.hash || '';
  const hashQueryIndex = nextHash.indexOf('?');
  if (hashQueryIndex !== -1) {
    // indexOf rather than split, so a malformed hash carrying a second '?' keeps everything
    // after it instead of having the tail silently dropped.
    nextHash = `${nextHash.slice(0, hashQueryIndex)}${strip(nextHash.slice(hashQueryIndex + 1))}`;
  }
  history.replaceState(null, '', `${browserLocation.pathname}${nextSearch}${nextHash}`);
};

const HomePage = (props) => {
  const leads = useLeadsSDK();
  const location = useLocation();
  const dispatch = useDispatch();
  // The root reducer mounts this slice as OrderState. Reading state.order returned undefined
  // on every selector, which is why the redemption state machine never gated anything.
  const attemptedSiteToken = useSelector((state) => state.OrderState?.attemptedSiteToken);
  const [loader, setLoader] = useState(true);
  const { sessionResponse } = useSessionData();
  // This app is a HashRouter, so location.search IS the hash query - which is where every
  // existing link puts its params, and what master parsed. The halo landing page can send the
  // token either side of the hash, so ONLY the token falls back to the pre-hash query string.
  // Widening that fallback to every param changed flow selection for ordinary visitors:
  // `/?servRef=123#/` began rendering VasOnly, and `/?gid=G1#/` began firing a session call
  // whose failure redirects the browser off checkout entirely.
  const queryParams = queryString.parse(location.search);
  const preHashParams = queryString.parse(window.location.search);
  const siteToken = queryParams['site-token'] || queryParams.site_token
    || preHashParams['site-token'] || preHashParams.site_token;
  const { servRef = '', mobileServRef = '', referrer = '' } = queryParams;
  const gid = (sessionResponse && sessionResponse.uid) || queryParams.gid;
  // Serviceability auto-fires /prequal on mount, and /prequal is what prices the cart. Hold
  // the whole page behind the loader until redemption has settled, or the first quote goes out
  // without the Friends & Family promo and the customer sees standard pricing.
  const [tokenProcessing, setTokenProcessing] = useState(Boolean(siteToken));
  const processedTokenRef = useRef('');

  useEffect(() => {
    const { invoca_rev: invocaRev, buyflow } = queryString.parse(location.search);
    if (invocaRev === 'draft') {
      sessionStorage.setItem('invocaTestMode', 'true');
    }
    if (buyflow === 'on' || buyflow === 'off') {
      sessionStorage.setItem('buyflowOverride', buyflow);
    }
    // The string, not the parsed object - queryString.parse returns a fresh object every
    // render, which re-ran this effect on every render instead of on navigation.
  }, [location.search]);

  useEffect(() => {
    if (!siteToken) {
      setTokenProcessing(false);
      return;
    }
    // Guard per token rather than with a global "redeemed" latch: the latch made a rejected
    // token permanent for the tab, so a second, valid link could never be redeemed. The ref
    // covers StrictMode's double-invoke and re-renders; redux covers a component remount.
    if (processedTokenRef.current === siteToken
      || attemptedSiteToken === siteToken
      || sessionStorage.getItem(sessionKeys.ffAttemptedToken) === siteToken) {
      setTokenProcessing(false);
      return;
    }
    processedTokenRef.current = siteToken;

    const processSiteToken = async () => {
      // Record the attempt and drop the token from the URL BEFORE awaiting. Redux and the ref
      // are both in-memory: a reload while the POST was in flight replayed a token the server
      // had already spent, which burned it and cost the customer the referral outright.
      sessionStorage.setItem(sessionKeys.ffAttemptedToken, siteToken);
      stripSiteTokenFromUrl();
      dispatch(setSiteToken(siteToken));
      try {
        // The reducer is what grants or refuses entitlement, off the same REDEEM_SITE_TOKEN
        // action this awaits. Nothing is written here: this branch only reports the outcome.
        const redeemRes = await dispatch(redeemSiteToken(siteToken, referrer));
        const { isVerified } = parseRedemptionClaims(redeemRes);
        if (isVerified) {
          pushFFEvent('ff_referrer_submitted', {
            ff_referrer_verified: true,
            ff_source: FF_SOURCE,
            promo_code: FF_PROMO_CODE,
          });
        } else {
          pushFFEvent('ff_token_unverified');
        }
      } catch (err) {
        // No response means the request never reached WordPress - a timeout, a DNS or TLS
        // failure, or an unresolvable host - so the token was not spent and the customer must
        // be able to retry it by re-opening their link. Drop the attempt marker in that case
        // only; a server that answered has spent the token and the marker must stand.
        if (!err?.response) {
          sessionStorage.removeItem(sessionKeys.ffAttemptedToken);
        }
        pushFFEvent('ff_token_redemption_failed');
      } finally {
        setTokenProcessing(false);
      }
    };
    processSiteToken();
  }, [dispatch, siteToken, referrer]);

  useEffect(() => {
    if (!isUndefinedNullOrEmpty(gid) && !sessionResponse.uid) {
      dispatch(sessionCall(gid)).then((response) => {
        const { agentId, partnerId } = response.value.data
        // Dispatched BEFORE create rather than after it. Every consumer reads this
        // slice at render time behind a query-param-first fallback
        // (hooks/order.js:651, util/buyflowOverride.js:129, Header.js:50), so none
        // depends on the old post-create timing - landing earlier only makes the
        // redux fallback available sooner. The payload stays RAW: the reducer
        // stores it verbatim and consumers normalize at read, so handing them the
        // sanitized form would silently change isInviteEarthlink and buyflow input.
        if (referrer) {
          dispatch(setReferrer(referrer))
        }
        const body = {
          orderStatus: OrderStatus.newSession,
          affiliateUserId: agentId,
          partnerName: partnerId,
        };
        // POST /initial-order-create persists the body verbatim, so an omitted key
        // is simply absent - which is what an empty referrer must be. Never write
        // '' or null (ELNKWEB-3218).
        const trackingReferrer = sanitizeTrackingReferrer(referrer);
        if (trackingReferrer) {
          body.trackingAttrs = { referrer: trackingReferrer };
        }
        dispatch(initialOrderCreate(body)).then((res) => {
          setSessionOrderAccessToken(res.value.data.orderAccessToken);
          setLoader(false)

          leads?.updateOrder?.('Initialize Order', {
            partner: {
              affiliate: {
                key: response?.value?.data?.affiliateKey,
                name: response?.value?.data?.affiliateName,
              },
              businessPromo: response?.value?.data?.businessPromoCode,
              id: response?.value?.data?.partnerId,
              promo: response?.value?.data?.promoCode,
            },
            session: {
              internalOrderId: res?.value?.data?.id,
              isBusiness: response?.value?.data?.businessFlag,
              isMaintenance: response?.value?.data?.maintenanceFlag,
              isVas: response?.value?.data?.vasFlag,
            },
          });
        }).catch(() => { window.location.href = appendInvocaTestModeParam(config.BASE_URL) })
      }).catch(() => { window.location.href = appendInvocaTestModeParam(config.BASE_URL) })
    } else {
      setLoader(false);
    }
  }, [dispatch, gid])

  if (loader || tokenProcessing) {
    return <BackdropLoader />
  }
  if (servRef) {
    return <VasOnly {...props} />;
  }
  if (mobileServRef) {
    return <MobileOnly  {...props} />
  }
  return <ServiceabilityCheck {...props} />;
}

export default React.memo(HomePage);
